import * as fabric from 'fabric'
import { EditorObjectType, TecnicaType } from '@utils/types'

export const MIN_ZOOM = 1
export const MAX_ZOOM = 20

export const MAX_POSIZIONI = 2

export const EDITOR_FROM_CART_MAIN = 'cart-main'
export const EDITOR_FROM_PDP = 'pdp'
export const EDITOR_FROM_CART_LATER = 'cart-later'
// Chiavi condivise tra editor e PDP per riprendere lo stesso preventivo.
export const EDITOR_PDP_CART_STORAGE_KEY = 'editor-carrello-id-pdp'
export const EDITOR_PDP_PERSONALIZZAZIONE_STORAGE_KEY = 'editor-personalizzazione-id-pdp'

export const EDITOR_FILE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "bmp", "ai", "eps", "pdf", "svg"]
export const EDITOR_MAX_UPLOAD_FILE_SIZE_MB = 10
export const EDITOR_MAX_UPLOAD_FILE_SIZE_BYTES = EDITOR_MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024
export const EDITOR_MIN_DPI = 300

export const getEditorUrl = (personalizzazione: any, ordine: any, editorSourceParam: string) => {
  if (!personalizzazione || !ordine?.id) return undefined

  return `/editor-bozze/${personalizzazione}/${ordine.id}?from=${editorSourceParam}`
}

export const clampPan = (
  canvas: fabric.Canvas,
  bounds?: { left: number; top: number; right: number; bottom: number }
) => {
  const vpt = canvas.viewportTransform
  const z = canvas.getZoom()
  const w = canvas.getWidth()
  const h = canvas.getHeight()

  let minX = w - w * z
  let maxX = 0
  let minY = h - h * z
  let maxY = 0

  if (bounds) {
    minX = w - bounds.right * z
    maxX = -bounds.left * z
    minY = h - bounds.bottom * z
    maxY = -bounds.top * z
  }

  if (vpt[4] < minX) vpt[4] = minX
  if (vpt[4] > maxX) vpt[4] = maxX
  if (vpt[5] < minY) vpt[5] = minY
  if (vpt[5] > maxY) vpt[5] = maxY
}

// Helper to generate unique IDs for fabric objects
export const generateUniqueId = () =>
  `fabric-obj-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`

export const isContained = (obj: fabric.Object, areaStampa: fabric.Object) => {
  const status = obj.isContainedWithinObject(areaStampa)
  return status
}

export const convertPxToCm = (value, reference, referenceCm) => {
  return parseFloat(((referenceCm * value) / reference).toFixed(2))
}

export const convertCmToPx = (value, reference, referencePx) => {
  return parseFloat(((referencePx * value) / reference).toFixed(2))
}

export const parseFontSize = (value: number) => {
  return value / 10
}

export const convertFontSize = (value: number) => {
  return value * 10
}

const hexToRgb = (hex) => {
  const parsedHex = splitHexColorAndAlpha(hex)
  if (!parsedHex) throw new Error('Invalid HEX color.')
  hex = parsedHex.rgbHex.replace(/^#/, '')

  // Split the hex color into three parts (r, g, b) and convert them to decimal.
  var r = parseInt(hex.slice(0, 2), 16)
  var g = parseInt(hex.slice(2, 4), 16)
  var b = parseInt(hex.slice(4, 6), 16)

  // Return the RGB representation.
  return {
    r,
    g,
    b,
  }
}

const isHexDigits = (value: string) => /^[0-9a-fA-F]+$/.test(value)

const alphaFloatToHex = (alpha: number) =>
  Math.max(0, Math.min(255, Math.round(alpha * 255)))
    .toString(16)
    .padStart(2, '0')

const parseHexColor = (value?: string) => {
  if (typeof value !== 'string') return null

  let clean = value.trim()
  if (!clean) return null
  if (clean[0] === '#') clean = clean.slice(1)
  if (!isHexDigits(clean)) return null

  const normalized = clean.toLowerCase()

  if (normalized.length === 3) {
    const rgb = normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('')

    return {
      rgb,
      alpha: 'ff',
      hasAlpha: false,
    }
  }

  if (normalized.length === 4) {
    const expanded = normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('')

    return {
      rgb: expanded.slice(0, 6),
      alpha: expanded.slice(6, 8),
      hasAlpha: true,
    }
  }

  if (normalized.length === 6) {
    return {
      rgb: normalized,
      alpha: 'ff',
      hasAlpha: false,
    }
  }

  if (normalized.length === 8) {
    return {
      rgb: normalized.slice(0, 6),
      alpha: normalized.slice(6, 8),
      hasAlpha: true,
    }
  }

  return null
}

export const normalizeHexColor = (value?: string) => {
  if (typeof value !== 'string') return ''

  let clean = value.trim()
  if (!clean) return ''

  if (clean.toLowerCase() === 'transparent') return '#00000000'

  const parsedHex = parseHexColor(clean)
  if (parsedHex) {
    if (parsedHex.alpha !== 'ff') return `#${parsedHex.rgb}${parsedHex.alpha}`
    return `#${parsedHex.rgb}`
  }

  try {
    const fabricColor = new fabric.Color(clean)
    const normalizedRgbHex = `#${fabricColor.toHex().toLowerCase()}`
    const alpha =
      typeof (fabricColor as any).getAlpha === 'function' ? (fabricColor as any).getAlpha() : 1

    if (Number.isFinite(alpha) && alpha < 1) {
      return `${normalizedRgbHex}${alphaFloatToHex(alpha)}`
    }

    return normalizedRgbHex
  } catch (error) {
    return clean.toLowerCase()
  }
}

export const isHexEqual = (hex1?: string, hex2?: string) => {
  const normalizedHex1 = normalizeHexColor(hex1)
  const normalizedHex2 = normalizeHexColor(hex2)
  return normalizedHex1 === normalizedHex2
}

export const splitHexColorAndAlpha = (value?: string) => {
  const normalized = normalizeHexColor(value)
  const parsedHex = parseHexColor(normalized)
  if (!parsedHex) return null

  return {
    rgbHex: `#${parsedHex.rgb}`,
    alphaHex: parsedHex.alpha,
    hasAlpha: parsedHex.hasAlpha,
  }
}

export const applyAlphaToHexColor = (rgbHex?: string, alphaHex?: string) => {
  const normalizedRgb = normalizeHexColor(rgbHex)
  const parsedRgb = parseHexColor(normalizedRgb)
  if (!parsedRgb) return normalizedRgb

  const normalizedAlpha = typeof alphaHex === 'string' ? alphaHex.trim().toLowerCase() : 'ff'
  if (!/^[0-9a-f]{2}$/.test(normalizedAlpha) || normalizedAlpha === 'ff') {
    return `#${parsedRgb.rgb}`
  }

  return `#${parsedRgb.rgb}${normalizedAlpha}`
}

type PantoneReferenceItem = {
  codice?: string
  nome?: string
  hex?: string
}

let pantoniReference: PantoneReferenceItem[] = []

export const setPantoniReference = (pantoni: PantoneReferenceItem[] = []) => {
  pantoniReference = Array.isArray(pantoni) ? pantoni.filter(Boolean) : []
}

export const getPantoniReference = () => pantoniReference

export const convertHexToCMYK = (hex) => {
  const { r, g, b } = hexToRgb(hex)

  /**
   * Converts RGB [0-255] to CMYK [0-100] using a simple formula.
   * WARNING: This is a rough approximation and ignores color profiles.
   */
  if (r === 0 && g === 0 && b === 0) {
    // Black
    return { c: 0, m: 0, y: 0, k: 100 }
  }

  // Normalize RGB to 0-1 range
  let rPrime = r / 255
  let gPrime = g / 255
  let bPrime = b / 255

  // Calculate K (black key)
  let k = 1.0 - Math.max(rPrime, gPrime, bPrime)

  let c, m, y

  // Avoid division by zero if K is 1 (pure black)
  if (k === 1.0) {
    c = 0
    m = 0
    y = 0
  } else {
    c = (1.0 - rPrime - k) / (1.0 - k)
    m = (1.0 - gPrime - k) / (1.0 - k)
    y = (1.0 - bPrime - k) / (1.0 - k)
  }

  // Return CMYK values in the 0-100 range
  return {
    c: Math.round(c * 100),
    m: Math.round(m * 100),
    y: Math.round(y * 100),
    k: Math.round(k * 100),
  }
}

export const convertHexToCmyk = (hex?: string) => {
  const value = typeof hex === 'string' ? hex : (hex as any)?.hex
  if (!value || typeof value !== 'string') return ''

  const parsedHex = splitHexColorAndAlpha(value)
  if (!parsedHex) return ''
  const clean = parsedHex.rgbHex.slice(1)

  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  const k = 1 - Math.max(r, g, b)
  if (k === 1) return '(0,0,0,100)'

  const denom = 1 - k
  const c = Math.round(((1 - r - k) / denom) * 100)
  const m = Math.round(((1 - g - k) / denom) * 100)
  const y = Math.round(((1 - b - k) / denom) * 100)
  const kPct = Math.round(k * 100)

  return `(${c},${m},${y},${kPct})`
}

export const convertHexToPantone = (hex, pantoni: PantoneReferenceItem[] = []) => {
  const parsedHex = splitHexColorAndAlpha(hex)
  if (!parsedHex) {
    return {
      codice: '',
      nome: '',
      hex: normalizeHexColor(hex),
    }
  }

  const { r: targetR, g: targetG, b: targetB } = hexToRgb(parsedHex.rgbHex)
  const sourcePantoni = Array.isArray(pantoni) && pantoni.length > 0 ? pantoni : pantoniReference

  // --- Find Nearest Color ---
  let minSquaredDistance = Number.POSITIVE_INFINITY // Initialize with infinity
  let nearestPantone = null // To store the best match found so far

  // Iterate through each Pantone entry in the loaded data
  for (const entry of sourcePantoni) {
    const normalizedHex = normalizeHexColor(entry?.hex || '')
    if (!normalizedHex) continue

    let rgb
    try {
      rgb = hexToRgb(normalizedHex)
    } catch (error) {
      continue
    }

    if (!rgb) continue

    const deltaR = targetR - rgb.r
    const deltaG = targetG - rgb.g
    const deltaB = targetB - rgb.b
    const squaredDistance = deltaR * deltaR + deltaG * deltaG + deltaB * deltaB

    if (squaredDistance < minSquaredDistance) {
      minSquaredDistance = squaredDistance
      nearestPantone = {
        codice: entry?.codice || '',
        nome: entry?.nome || '',
        hex: normalizedHex,
      }
    }
  }

  if (nearestPantone === null) {
    return {
      codice: '',
      nome: '',
      hex: applyAlphaToHexColor(parsedHex.rgbHex, parsedHex.alphaHex),
    }
  }

  const pantoneHexWithAlpha = applyAlphaToHexColor(nearestPantone.hex, parsedHex.alphaHex)

  return nearestPantone
    ? {
        ...nearestPantone,
        hex: pantoneHexWithAlpha,
      }
    : {
        codice: '',
        nome: '',
        hex: pantoneHexWithAlpha,
      }
}

export const convertColorToHex = (variant?: 'pantone' | 'cmyk', code?: string) => {
  // '#FF0000'
  const value = typeof code === 'string' ? code.trim() : ''
  if (!value) return ''

  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0')

  if (variant === 'pantone') {
    if (value[0] === '#') return normalizeHexColor(value)
    let hash = 0
    for (let i = 0; i < value.length; i += 1) {
      hash = (hash * 31 + value.charCodeAt(i)) >>> 0
    }
    const r = hash & 0xff
    const g = (hash >>> 8) & 0xff
    const b = (hash >>> 16) & 0xff
    return normalizeHexColor(`#${toHex(r)}${toHex(g)}${toHex(b)}`)
  }

  const numbers = value.match(/-?\d+(\.\d+)?/g)
  if (!numbers || numbers.length < 4) return ''
  const [c, m, y, k] = numbers.slice(0, 4).map((n) => parseFloat(n))
  if ([c, m, y, k].some((n) => Number.isNaN(n))) return ''

  const r = 255 * (1 - c / 100) * (1 - k / 100)
  const g = 255 * (1 - m / 100) * (1 - k / 100)
  const b = 255 * (1 - y / 100) * (1 - k / 100)

  return normalizeHexColor(`#${toHex(r)}${toHex(g)}${toHex(b)}`)
}

export const DEFAULT_OBJECT_CONTROL: EditorObjectType = {
  id: '',
  top: undefined,
  fill: undefined,
  left: undefined,
  text: '',
  angle: 0,
  etype: '',
  image: '',
  width: undefined,
  colori: [],
  colore: undefined,
  height: undefined,
  filename: '',
  isVector: undefined,
  isSelected: undefined,
  noBackground: undefined,
  hasBackground: undefined,
  removedBackgroundColor: undefined,
  removedBackgroundIndex: 0,
  fontFamily: undefined,
  fontSize: undefined,
  fontStyle: undefined,
  fontWeight: undefined,
  monochrome: undefined,
  underline: undefined,
  editorMeta: undefined,
}

export const hasEditorPerm = (utente: any) => {
  return utente && utente?.is_staff // TODO
}

export const isImageCompatibleWithTecnica = (image: any, tecnica: TecnicaType) => {
  if (!tecnica) return true

  const allowedExtensions = (tecnica?.estensioni_ammesse || [])
    .map((ext) => ext.replace(/^\./, '').toLowerCase())
    .filter(Boolean)
  const imageExtension = image?.editorMeta?.extension

  if (imageExtension && allowedExtensions.length > 0) {
    return allowedExtensions.includes(imageExtension)
  }

  const isVector = image?.isVector ?? image?.editorMeta?.is_vector
  if (isVector === true) return tecnica?.accetta_vettoriale !== false
  if (isVector === false) return tecnica?.accetta_raster !== false

  return true
}
